import { Button } from "@/components/ui/button";
import { CycleDetails } from "../cycle-details";
import { PackageDetails } from "../package-details";
import { AdditionalPaymentsTable } from "../additional-payments-table";
import { UpsellCyclesTable } from "../updated-cycles-table";
import { BookingFlowHeader } from "../booking-flow-header";
import { usePoContractsUpdateFlow } from "../../context/use-po-contracts-update-flow/use-po-contracts-update-flow";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ContractComparison } from "../contract-comparison";
import { PoContractUpdateSimulation } from "../po-contracts-update-simulation";
import { useEffect } from "react";
import Masker from "vanilla-masker";
import { formatInTimeZone } from "date-fns-tz";
import { getPoTypeLabel } from "@/features/license-fee/list/utils/private-office-contract-mappings";
import { usePoContractProrataMemoryPreview } from "@/hooks/use-po-contract-prorata-memory-preview/use-po-contract-prorata-memory-preview";
import { PrivateOfficeProRataMemory } from "@/components/private-office-prorata-memory";
import { safeFormatInTimeZone } from "@/utils/format/safe-format-in-time-zone";

const formatuCurrency = (value?: string | number) => {
  if (!value) return "";

  const normalizedValue = parseFloat(`${value}`).toFixed(2);

  return Masker.toMoney(normalizedValue, {
    precision: 2,
    separator: ",",
    delimiter: ".",
  });
};

export const PoContractsUpdateSummary = () => {
  const {
    poContractUpdateData,
    upsellData,
    previousStep,
    nextStep,
    flowType,
    selectedCycle,
    selectedPackage,
    selectedPackageGroups,
    poInitialContractData,
    simulatedUpdate: { toCreate, toUpdate, toCancel },
    SimulateUpdate,
  } = usePoContractsUpdateFlow();

  // Data a partir da qual a edição passa a valer. É a data de início nova
  // quando o operador a alterou e, caso contrário, a do contrato original —
  // a mesma regra usada no texto "Atualizações começarão em" abaixo, para
  // que a memória exibida seja sempre a da data mostrada na tela.
  const effectiveStartDate =
    poContractUpdateData?.startDate || poInitialContractData?.startDate;

  // Memória da vigência EDITADA, antes de confirmar (PED-13997): a prévia
  // recebe as datas/créditos/take rate do formulário. A memória do contrato
  // salvo (`GET /contracts/:id/prorata-memory`) usaria a vigência antiga e
  // mostraria o cálculo que a edição vai substituir.
  // Só contrato friccao_zero com pró-rata aplicável tem pró-rata nativo.
  const hasNativeProrata =
    poInitialContractData?.commercialPolicy === "friccao_zero" &&
    Boolean(poInitialContractData?.prorataApplicable);
  const isUndeterminedTime =
    poContractUpdateData?.hasUndeterminedTime ??
    poInitialContractData?.hasUndeterminedTime;
  const effectiveEndDate = isUndeterminedTime
    ? undefined
    : poContractUpdateData?.endDate || poInitialContractData?.endDate;
  const effectiveMonthlyCredits = Number(
    poContractUpdateData?.monthlyCredits ??
      poInitialContractData?.monthlyCredits,
  );
  const effectiveTakePercentage = Number(
    poContractUpdateData?.takeRatePercentage ??
      poInitialContractData?.takeRatePercentage,
  );

  const { proRataMemory, isLoadingProRataMemory, proRataMemoryError } =
    usePoContractProrataMemoryPreview({
      packageId: hasNativeProrata
        ? Number(
            poContractUpdateData?.packageId ?? poInitialContractData?.packageId,
          )
        : undefined,
      companyId: hasNativeProrata
        ? Number(poContractUpdateData?.company?.id)
        : undefined,
      // `date` é date-only (yyyy-MM-dd): formatamos em UTC, como todas as
      // outras datas de contrato nesta tela, para que a data enviada seja
      // exatamente a exibida, sem deslocar um dia pelo fuso do navegador.
      startDate: hasNativeProrata
        ? safeFormatInTimeZone(effectiveStartDate, "UTC", "yyyy-MM-dd", "")
        : undefined,
      endDate: safeFormatInTimeZone(effectiveEndDate, "UTC", "yyyy-MM-dd"),
      monthlyCredits: Number.isFinite(effectiveMonthlyCredits)
        ? effectiveMonthlyCredits
        : undefined,
      takePercentage: Number.isFinite(effectiveTakePercentage)
        ? effectiveTakePercentage
        : undefined,
    });

  useEffect(() => {
    SimulateUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- roda uma única vez ao entrar no resumo: os dados do fluxo já estão congelados neste passo.
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <BookingFlowHeader
        onClick={() => previousStep()}
        title="Editando contrato de venda de escritório"
        subtitle="Associe um escritório privativo à uma empresa contratante."
      />
      <div className="space-y-6 mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="self-stretch text-foreground text-2xl font-medium">
              Pacote e Ciclo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Card className="flex bg-slate-50">
              {selectedPackage && selectedPackageGroups && (
                <div className="flex-1">
                  <PackageDetails
                    selectedPackage={selectedPackage}
                    packageGroups={selectedPackageGroups}
                    className="border-r-2"
                  />
                </div>
              )}
              {selectedCycle && (
                <div className="flex-1">
                  <CycleDetails
                    activeCycle={selectedCycle}
                    additionalCredits={
                      Number(poContractUpdateData?.monthlyCredits) || 0
                    }
                    upsellCredits={upsellData?.upsellOption || 0}
                  />
                </div>
              )}
            </Card>
          </CardContent>
        </Card>
        {poInitialContractData && poContractUpdateData && (
          <ContractComparison
            initialContractData={{
              ...poInitialContractData,
              partnerValue: formatuCurrency(poInitialContractData.partnerValue),
              poType: getPoTypeLabel(
                poInitialContractData.poType,
              ) as typeof poInitialContractData.poType,
            }}
            updatedContractData={{
              ...poContractUpdateData,
              poType: getPoTypeLabel(
                poContractUpdateData.poType,
              ) as typeof poContractUpdateData.poType,
            }}
          />
        )}
        {!!(toCreate.count > 0 || toUpdate.count > 0 || toCancel.count > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="self-stretch text-foreground text-2xl font-medium">
                Bookings Afetados
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground leading-tight pb-2 whitespace-nowrap">
                Como os próximos bookings ficarão após a edição
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-muted-foreground">
                    Atualizações começarão em :{" "}
                    <span className="font-medium text-black">
                      {safeFormatInTimeZone(
                        effectiveStartDate,
                        "UTC",
                        "dd/MM/yyyy",
                        "-",
                      )}
                    </span>
                  </p>
                </div>
              </div>
              <PoContractUpdateSimulation
                toCreate={toCreate}
                toUpdate={toUpdate}
                toCancel={toCancel}
              />
            </CardContent>
          </Card>
        )}
        {flowType === "upsell" && upsellData && (
          <>
            <Card className="w-full px-2">
              <CardHeader>
                <CardTitle className="self-stretch text-foreground text-2xl font-medium">
                  Pagamentos adicionais
                </CardTitle>
                <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                  {upsellData?.simulatedUpsellData.newPayments &&
                    "Os seguintes pagamentos serão cobrados."}
                </CardDescription>
                {upsellData?.startCycle && (
                  <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                    {`Ciclo de início: ${upsellData.startCycle.id} — ${formatInTimeZone(
                      upsellData.startCycle.startDate,
                      "UTC",
                      "dd/MM/yyyy",
                    )}`}
                  </CardDescription>
                )}
              </CardHeader>
              {upsellData?.simulatedUpsellData.newPayments && (
                <CardContent>
                  <AdditionalPaymentsTable
                    data={upsellData?.simulatedUpsellData.newPayments}
                    showInvoicingCompetency
                  />
                </CardContent>
              )}
            </Card>

            <Card className="w-full px-2">
              <CardHeader>
                <CardTitle className="self-stretch text-foreground text-2xl font-medium">
                  Ciclos afetados
                </CardTitle>
                <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                  {upsellData?.simulatedUpsellData?.updatedCycles
                    ? "Revise abaixo como os ciclos ao longo da reserva serão impactados por ela."
                    : "Selecione um valor de créditos adicionais para mais informações."}
                </CardDescription>
              </CardHeader>
              {upsellData?.simulatedUpsellData?.updatedCycles && (
                <CardContent>
                  <UpsellCyclesTable
                    data={upsellData?.simulatedUpsellData.updatedCycles}
                  />
                </CardContent>
              )}
            </Card>
          </>
        )}
        <PrivateOfficeProRataMemory
          data={proRataMemory}
          isLoading={isLoadingProRataMemory}
          error={proRataMemoryError}
        />
      </div>

      <div className="mt-8 flex justify-end gap-4">
        <Button variant="outline" onClick={previousStep} className="px-4 py-2">
          Voltar
        </Button>
        <Button onClick={nextStep} className="px-4 py-2">
          Concluir edição
        </Button>
      </div>
    </div>
  );
};
